import { useLinkSigner } from '../../store/link/hooks'
import { useDispatch } from 'react-redux'
import { useAccountInfo } from '../../store/account/hooks'
import { useCallback } from 'react'
import { Address, L1ChainId, TokenId, TokenSymbol } from '../../types'
import { BigNumber } from 'ethers'
import { updateWithdrawing } from '../../store/withdraw/actions'
import { findCurrencyById } from '../../store/market/hooks'
import { getGatewayScheduling } from '../../api/v1/scheduling'
import { store } from '../../store'
import { e2w, toFixed, w2e } from '../../utils/number'
import { getAccountNonce } from '../../api/v1/account'
import {
  signWithdraw,
  SignWithdrawPayload,
} from '../../utils/signer/transactions/withdraw'
import { SUB_ACCOUNT_ID } from '../../config'
import { postWithdraw, PostWithdrawBody } from '../../api/v1/withdraw'
import toastify from '../../components/Toastify'
import i18n from '../../i18n'
import { updateModal } from '../../store/app/actions/modalActions'
import { zklinkExplorerUrl } from 'utils/explorer'
import { NetworkRegistry } from '../../utils/networkRegistry'
import { UserRejectedError } from 'utils/errors/userReject'

export function useWithdraw() {
  const signer = useLinkSigner()
  const dispatch = useDispatch()
  const accountInfo = useAccountInfo()
  return useCallback(
    async function withdraw({
      address,
      tokenId,
      tokenSymbol,
      amount,
      layerOneChainId,
      fee,
      withdrawFeeRatio,
      fastWithdraw,
    }: {
      address: Address
      tokenId: TokenId
      tokenSymbol: TokenSymbol
      amount: BigNumber
      layerOneChainId: L1ChainId
      fee: BigNumber
      withdrawFeeRatio: number
      fastWithdraw: 0 | 1
    }) {
      try {
        if (!signer) {
          return
        }
        dispatch(updateWithdrawing(true))

        const currentNetwork = NetworkRegistry.byL1(layerOneChainId)
        const currentToken = findCurrencyById({
          l2Id: tokenId,
        })

        if (!currentNetwork) {
          throw new Error(`Unknown network layer1 chain id: ${layerOneChainId}`)
        }

        let { gateway } = currentNetwork

        let toChainId = currentNetwork.layerTwoChainId
        let withdrawToL1: boolean = false
        // Withdraw to Ethereum requires fetching the preferred gateway from the gateway scheduling system.
        if (gateway) {
          const priorityGateway = await getGatewayScheduling(
            store.getState().app.ethProperty.gateways,
          )

          if (!priorityGateway) {
            throw new Error(`No available gateway found`)
          }

          toChainId = priorityGateway.chainId
          withdrawToL1 = true
        }

        if (!toChainId) {
          throw new Error(`Unknown network layer2 chain id: ${toChainId}`)
        }

        const tokenDecimals = currentToken?.chains[toChainId]?.decimals ?? 0

        // Discard quantities beyond the effective precision.
        amount = e2w(toFixed(w2e(amount), tokenDecimals))

        const { nonce } = await getAccountNonce(2).then((r) => r.data)
        const { layer2Id = 0 } = accountInfo
        const payload: SignWithdrawPayload = {
          accountId: layer2Id,
          toChainId: toChainId,
          subAccountId: SUB_ACCOUNT_ID,
          to: address,
          l2SourceTokenId: tokenId,
          l2SourceTokenSymbol: tokenSymbol,
          l1TargetTokenId: tokenId,
          amount: amount.toString(),
          tokenDecimals,
          fee: fee.toString(),
          withdrawFeeRatio: fastWithdraw ? withdrawFeeRatio : 0,
          withdrawToL1,
          nonce: nonce,
        }

        const signedData = await signWithdraw(signer, payload)

        const { tx, layer1_signature } = signedData
        const body: PostWithdrawBody = {
          currency: tokenSymbol.toUpperCase(), // Convert wBTC to WBTC, zkex only supports uppercase currency symbols.
          toChainId: tx.toChainId, // 3
          to: tx.to, // '0x00...0000'
          fee: w2e(tx.fee), // '0.001'
          amount: w2e(tx.amount), // '100.1'
          withdrawToL1: tx.withdrawToL1, // true
          withdrawFeeRatio: tx.withdrawFeeRatio, // 100
          nonce: tx.nonce, // 0
          ts: tx.ts, // 1684419923
          signature: tx.signature.signature,
          ethSignature: layer1_signature!,
        }
        const rpcResponse = await postWithdraw(0, body)

        toastify.transaction(
          i18n.t('common-withdraw-submitted'),
          zklinkExplorerUrl(rpcResponse.data.txHash),
        )

        dispatch(updateModal({ modal: 'withdraw', open: false }))

        return Promise.resolve()
      } catch (e) {
        if (UserRejectedError.is(e)) {
        } else {
          console.error(e)
          toastify.error(e.message)
        }
      } finally {
        dispatch(updateWithdrawing(false))
      }
    },
    [signer, accountInfo],
  )
}
